import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
  Box,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { TableCellRenderer } from "./TableCellRenderers";

export enum RendererType {
  TEXT = "text",
  CURRENCY = "currency",
  NUMBER = "number",
  URL = "url",
  MARKDOWN = "markdown",
  LIST = "list",
  LINKIFIED_TEXT = "linkifiedText",
  PERCENTAGE = "percentage",
}

export enum ListType {
  ORDERED = "ol",
  UNORDERED = "ul",
}

export interface ColumnConfig {
  name: string;
  field?: string; // Optional: field name in data. If not provided, uses 'name'
  minWidth?: string;
  maxWidth?: string;
  width?: string;
  align?: "left" | "center" | "right";
  hidden?: boolean;
  rendererType?: RendererType;
  listType?: ListType;
  onClick?: (value: any, row: any) => void;
}

interface DataTableProps {
  data: any[];
  loading?: boolean;
  maxHeight?: string;
  maxRows?: number;
  title?: string;
  columnConfigs?: ColumnConfig[];
  defaultColumnWidth?: string;

  /** Pagination props */
  totalCount?: number;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /** Sorting props */
  sortColumn?: string | null;
  sortDirection?: "asc" | "desc";
  onSort?: (column: string) => void;

  /** Row click handler */
  onRowClick?: (row: any) => void;
}

export const DataTable: React.FC<DataTableProps> = ({
  data,
  loading = false,
  maxHeight = "500px",
  maxRows,
  title,
  columnConfigs = [],
  defaultColumnWidth = "unset",
  totalCount = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  sortColumn = null,
  sortDirection = "asc",
  onSort,
  onRowClick,
}) => {
  /** If columnConfigs is provided and not empty, use it to determine column order and visibility */
  const columns = React.useMemo((): ColumnConfig[] => {
    /** Get columns from first row of data */
    const allColumns = data && data.length > 0 ? Object.keys(data[0]) : [];
    if (columnConfigs && columnConfigs.length > 0) {
      // Use columnConfigs to determine which columns to show and in what order
      const visibleConfigs = columnConfigs.filter((config) => !config.hidden);
      
      // If we have data, filter to only columns that exist in data
      // If no data (e.g., during loading), show all configured columns
      if (allColumns.length > 0) {
        return visibleConfigs.filter((config) => {
          const fieldName = config.field || config.name;
          return allColumns.includes(fieldName);
        });
      } else {
        // No data yet - show all configured columns (e.g., during initial load)
        return visibleConfigs;
      }
    } else {
      // Fallback: use all columns from data - create configs with name only
      return allColumns.map((col) => ({ name: col } as ColumnConfig));
    }
  }, [data, columnConfigs]);

  /** Helper to get field name from config */
  const getFieldName = (config: ColumnConfig): string => {
    return config.field || config.name;
  };

  /** Limit rows if maxRows is specified */
  const displayData = maxRows && data ? data.slice(0, maxRows) : data || [];

  return (
    <>
      {title && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "1.125rem",
            color: "#1e293b",
            mb: 2,
          }}
        >
          {title}
        </Typography>
      )}

      <Box sx={{ position: "relative", minHeight: maxHeight }}>
        <TableContainer
          sx={{
            height: maxHeight,
            minHeight: maxHeight,
            overflow: "auto",
            border: "1px solid #e2e8f0",
            borderRadius: 2,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: "red" }}>
              <TableRow>
                {columns.map((config) => {
                  const fieldName = getFieldName(config);
                  return (
                    <TableCell
                      key={config.name}
                      align={config.align || "left"}
                      sx={{
                        fontWeight: 600,
                        bgcolor: "#e9ecef",
                        fontSize: "0.7rem",
                        borderBottom: "2px solid #e2e8f0",
                        color: "#475569",
                        // backgroundColor: "grey",
                        py: 1.25,
                        lineHeight: 1.2,
                        minWidth: config?.minWidth || defaultColumnWidth,
                        maxWidth: config?.maxWidth,
                        width: config?.width,
                        zIndex: 11,
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}
                    >
                      {onSort ? (
                        <TableSortLabel
                          active={sortColumn === fieldName}
                          direction={
                            sortColumn === fieldName ? sortDirection : "asc"
                          }
                          onClick={() => onSort(fieldName)}
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent:
                              config.align === "right"
                                ? "flex-end"
                                : config.align === "center"
                                ? "center"
                                : "flex-start",
                            whiteSpace: "normal",
                            textAlign: config.align || "left",
                            "&.MuiTableSortLabel-root": {
                              color: "#475569",
                              fontSize: "0.7rem",
                            },
                            "&.MuiTableSortLabel-root:hover": {
                              color: "#1e293b",
                            },
                            "&.Mui-active": {
                              color: "#1e293b",
                            },
                            "& .MuiTableSortLabel-icon": {
                              fontSize: "0.875rem",
                            },
                          }}
                        >
                          {config.name}
                        </TableSortLabel>
                      ) : (
                        config.name
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Loading State - show when loading and no data */}
              {loading && displayData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(columns.length, 1)}
                    sx={{ textAlign: "center", py: 8 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
                      <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                        Loading data...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : displayData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(columns.length, 1)}
                    sx={{ textAlign: "center", py: 8 }}
                  >
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayData.map((row, index) => (
                  <TableRow
                    key={`row-${index}`}
                    sx={{
                      "&:hover": { backgroundColor: "#f8fafc" },
                      borderBottom: "1px solid #f1f5f9",
                      cursor: onRowClick ? "pointer" : "default",
                    }}
                  >
                    {columns.map((config) => {
                      const fieldName = getFieldName(config);
                      const clickHandler = config?.onClick
                        ? () => config.onClick!(row[fieldName], row)
                        : undefined;

                      return (
                        <TableCell
                          onClick={() =>
                            fieldName === "Order Number"
                              ? null
                              : onRowClick?.(row)
                          }
                          key={config.name}
                          align={config?.align || "left"}
                          sx={{
                            py: 1,
                            px: 1.5,
                            minWidth: config?.minWidth || defaultColumnWidth,
                            maxWidth: config?.maxWidth,
                            width: config?.width,
                          }}
                        >
                          <TableCellRenderer
                            value={row[fieldName]}
                            columnName={config.name}
                            onClick={clickHandler}
                            rendererType={config?.rendererType}
                            listType={config?.listType}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Loading Overlay - positioned over table body only, below sticky header */}
        {loading && displayData.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 48, // Height of sticky header row
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(255, 255, 255, 0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              backdropFilter: "blur(2px)",
              borderRadius: "0 0 8px 8px",
            }}
          >
            <CircularProgress size={30} sx={{ color: "#3b82f6" }} />
          </Box>
        )}
      </Box>

      {onPageChange && onRowsPerPageChange && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: "1px solid #e2e8f0",
            bgcolor: "white",
          }}
        />
      )}
    </>
  );
};
